import React, { useState } from 'react';
import { Smartphone, Download, Share, Plus, WifiOff, Zap, Check } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { usePwaInstall } from '../hooks/usePwaInstall';

export const InstallCard = () => {
  const { t } = useLanguage();
  const { canInstall, installed, isIos, promptInstall } = usePwaInstall();
  const [showSteps, setShowSteps] = useState(false);

  // Nothing to offer once it runs as an installed app, or when the browser cannot install it
  if (installed || (!canInstall && !isIos)) return null;

  return (
    <Card
      className="bg-gradient-to-br from-[#32CD32]/10 via-[#121212] to-[#121212] border-[#32CD32]/25 overflow-hidden relative"
      data-testid="install-card"
    >
      <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#32CD32]/5 blur-2xl" />
      <CardContent className="p-5 sm:p-6 relative">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="w-12 h-12 rounded-2xl bg-[#32CD32]/15 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-[#32CD32]" />
          </div>

          <div className="flex-1 min-w-0">
            <h3
              className="text-lg uppercase tracking-tight font-bold"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              {t('installCardTitle')}
            </h3>
            <p className="text-sm text-gray-400 mt-1">{t('installSubtitle')}</p>

            <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3">
              <span className="flex items-center gap-1.5 text-xs text-gray-300">
                <Zap className="w-3.5 h-3.5 text-[#32CD32]" />
                {t('installBullet1')}
              </span>
              <span className="flex items-center gap-1.5 text-xs text-gray-300">
                <WifiOff className="w-3.5 h-3.5 text-[#32CD32]" />
                {t('installBullet2')}
              </span>
              <span className="flex items-center gap-1.5 text-xs text-gray-300">
                <Check className="w-3.5 h-3.5 text-[#32CD32]" />
                {t('installBullet3')}
              </span>
            </div>

            {showSteps && (
              <div className="mt-4 space-y-1.5 text-xs text-gray-300" data-testid="install-card-ios-steps">
                <p className="flex items-center gap-1.5">
                  <span className="text-[#32CD32] font-semibold">1.</span>
                  {t('installIosStep1')}
                  <Share className="w-3.5 h-3.5 text-[#32CD32]" />
                </p>
                <p className="flex items-center gap-1.5">
                  <span className="text-[#32CD32] font-semibold">2.</span>
                  {t('installIosStep2')}
                  <Plus className="w-3.5 h-3.5 text-[#32CD32]" />
                </p>
              </div>
            )}
          </div>

          {canInstall ? (
            <Button
              onClick={promptInstall}
              className="btn-primary h-11 rounded-xl flex-shrink-0 mr-12 sm:mr-0"
              data-testid="install-card-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {t('installAction')}
            </Button>
          ) : (
            <Button
              onClick={() => setShowSteps((v) => !v)}
              variant="outline"
              className="h-11 rounded-xl border-[#32CD32]/40 text-[#32CD32] hover:bg-[#32CD32]/10 flex-shrink-0 mr-12 sm:mr-0"
              data-testid="install-card-howto-btn"
            >
              <Share className="w-4 h-4 mr-2" />
              {t('installHowTo')}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default InstallCard;
